import { useState, useEffect } from 'react';
import { SelectedVehicle } from './VehicleSelectionForm.js';
import { getSavedVehicles, addSavedVehicle, SavedVehicle } from '../vehicleHistory.js';
import { apiPublic } from '../api.js';
import { ProductType, useVehicleModalStore } from '../state/modalStore.js';

type FitmentStatus = 'idle' | 'loading' | 'fits' | 'doesNotFit' | 'error';

interface ProductFitmentCheckerProps {
  sku: string;
  ptype: ProductType
}

export function ProductFitmentChecker({ sku, ptype }: ProductFitmentCheckerProps) {
  const [status, setStatus] = useState<FitmentStatus>('idle');
  const [checkedVehicle, setCheckedVehicle] = useState<SavedVehicle | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const openModal = useVehicleModalStore(state => state.openModal);

  const isClearance = ptype === 'clearance';

  const checkFitment = async (vehicle: SavedVehicle) => {
    setStatus('loading');
    setCheckedVehicle(vehicle);
    try {
      const response = await apiPublic<{ fits: boolean }>(`/check-fitment?sku=${encodeURIComponent(sku)}&make_slug=${vehicle.makeSlug}&model_slug=${vehicle.modelSlug}&year=${vehicle.year}&ptype=${ptype}`);
      setStatus(response.data.fits ? 'fits' : 'doesNotFit');
    } catch (error) {
      console.error("Error checking fitment", error);
      setStatus('error');
      setErrorMessage('Could not check fitment. Please try again.');
    }
  };

  // This is the callback that will be passed to the modal.
  const handleVehicleSelectedFromModal = (vehicle: SelectedVehicle) => {
    const vehicleToSave: SavedVehicle = {
      makeName: vehicle.makeName,
      modelName: vehicle.modelName,
      year: vehicle.year,
      makeSlug: vehicle.makeSlug,
      modelSlug: vehicle.modelSlug,
    };
    addSavedVehicle(vehicleToSave);
    checkFitment(vehicleToSave);
  };

  const handleVehicleSelectForSearch = (vehicle: SelectedVehicle) => {
    addSavedVehicle({ makeName: vehicle.makeName, modelName: vehicle.modelName, year: vehicle.year, makeSlug: vehicle.makeSlug, modelSlug: vehicle.modelSlug });
    const uroot = isClearance ? "/clearance-fitment" : "/fitment"
    window.location.href = `/${uroot}/${vehicle.makeSlug}/${vehicle.modelSlug}/${vehicle.year}/`;
  };

  const handleOpenModalClick = () => {
    openModal(handleVehicleSelectedFromModal, sku);
  };

  const handleOpenModalForSearchClick = () => {
    openModal(handleVehicleSelectForSearch);
  };

  const handleSearchClick = () => {
    if (checkedVehicle) {
      const uroot = isClearance ? "/clearance-fitment" : "/fitment"
      window.location.assign(`${uroot}/` 
        + [ checkedVehicle.makeSlug, checkedVehicle.modelSlug, checkedVehicle.year].map(p => encodeURIComponent(p)).join('/')
      );
    }
  };

  // On initial mount, check the most recent vehicle from history.
  useEffect(() => {
    const savedVehicles = getSavedVehicles();
    if (savedVehicles.length > 0) {
      checkFitment(savedVehicles[0]);
    }
  }, [sku]); // eslint-disable-line react-hooks/exhaustive-deps


  if (status === 'idle' && !checkedVehicle) {
    return (
      <div className="gbrakes-product-fitment-checker">
        <h3 className="gbs-check-title">Check Fitment for Your Vehicle</h3>
        <button type="button" className="gbs-button" onClick={handleOpenModalClick}>Select Vehicle</button>
      </div>
    );
  }

  const vehicleName = checkedVehicle ? `${checkedVehicle.year} ${checkedVehicle.makeName} ${checkedVehicle.modelName}` : '';

  return (
    <div className="gbrakes-product-fitment-checker">
      {status === 'loading' && (
        <div className="gbs-fitment-result gbs-loading">
          <span className="gbs-spinner"></span>
          Checking fitment for your <strong>{vehicleName}</strong>...
        </div>
        )}
      {status === 'fits' && (
        <div className="gbs-fitment-result gbs-fits">
          <p>✅ This part fits your <strong>{vehicleName}</strong>.</p>

          <div className="gbs-fitment-check-new-vehicle">
            <button type="button" className="gbs-link-button" onClick={handleOpenModalClick}>Check a different Vehicle</button>
          </div>
        </div>
      )}
      {status === 'doesNotFit' && (
        <div className="gbs-fitment-result gbs-no-fit">
          <p>❌ This part does not fit your <strong>{vehicleName}</strong>.</p>

          <div className="gbs-fitment-check-new-vehicle">
            <button type="button" className="gbs-link-button" onClick={handleOpenModalClick}>Check a different Vehicle</button>
          </div>
        </div>
      )}
      {status === 'error' && (
        <div className="gbs-fitment-result gbs-error">
          <p>Error: {errorMessage}</p>
          <button type="button" className="gbs-link-button" onClick={handleOpenModalClick}>Try Again</button>
        </div>
      )}

      {status !== 'error' && (
        <div className="gbs-fitment-actions">
          {checkedVehicle &&
          <div className="gbs-fitment-search-my-vehicle">
            <button type="button" className="gbs-link-button" onClick={handleSearchClick}
              >More parts for your <strong>{vehicleName}</strong></button>
          </div>
          }

          {checkedVehicle &&
          <div className="gbs-fitment-search-new-vehicle">
            <button type="button" className="gbs-link-button" onClick={handleOpenModalForSearchClick}>Find parts for a different Vehicle</button>
          </div>
          }
        </div>
      )}
    </div>
  );
}